import { AppDestroy, provideAppDestroy } from '@bcf-vanilla-ts-v1-shared/di/app-destroy';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { asPromise } from '@bcf-vanilla-ts-v1-shared/misc/rx-helpers/as-promise';
import { transferRxNext } from '@bcf-vanilla-ts-v1-shared/misc/rx-helpers/transfer-rx-next';
import { isSSR } from '@bcf-vanilla-ts-v1-shared/misc/utils/is-ssr';
import { fromEvent, mapTo, merge, Observable, of, ReplaySubject, shareReplay, startWith, takeUntil, tap } from 'rxjs';

export type BeforeInstallPromptEvent = {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<UserChoice>;

  prompt(): Promise<void>;
  preventDefault(): void;
};

type UserChoice = {
  outcome: 'accepted' | 'dismissed';
  platform: string;
};

export class PwaService {
  public beforeInstallPromptEvent$: ReplaySubject<BeforeInstallPromptEvent> =
    new ReplaySubject<BeforeInstallPromptEvent>(1);

  private _getIsAppInstalledShareRef$!: Observable<boolean | undefined>;

  constructor(private _appDestroy: AppDestroy) {}

  public init(): void {
    if (isSSR()) {
      return;
    }
    fromEvent<BeforeInstallPromptEvent>(window, 'beforeinstallprompt')
      .pipe(
        tap((e: BeforeInstallPromptEvent) => e.preventDefault()),
        takeUntil(this._appDestroy.destroy$)
      )
      .subscribe(transferRxNext(this.beforeInstallPromptEvent$));
  }

  public isAppInstalled(): Observable<boolean | undefined> {
    if (isSSR()) {
      return of(true);
    }
    return (this._getIsAppInstalledShareRef$ ??= merge(
      this.beforeInstallPromptEvent$.pipe(mapTo(false)),
      fromEvent(window, 'appinstalled').pipe(mapTo(true))
    ).pipe(startWith(undefined), shareReplay({ bufferSize: 1, refCount: true })));
  }

  public async install(): Promise<void> {
    const beforeInstallEvent: BeforeInstallPromptEvent = await asPromise(this.beforeInstallPromptEvent$);
    beforeInstallEvent.prompt();
  }
}

export function providePwaService(): PwaService {
  return provideSingleton(PwaService, () => new PwaService(provideAppDestroy()));
}
